import { DistanceUnit, HeavyVehicleFlowType } from "@keita/lanepatrol-sdk";
import { HlaLane, CreateOrUpdateHlaLane } from "./HlaLane";

export interface HlaWay {
  id: string;
  campaignId: string;
  name: string | null;
  type: HlaWayType;
  coordinates: number[][];
  lengthM: number;
  averageDailyTraffic: number | null;
  averageRoadSpeed: number | null;
  frontFacingLane: HlaLane | null;
  backFacingLane: HlaLane | null;
  heavyVehicleFlow: HeavyVehicleFlowType | null;
  lit: boolean;
  verified: boolean;
  createdAt: string;
}

export enum HlaWayType {
  Cycleway = "Cycleway",
  Motorway = "Motorway",
  Primary = "Primary",
  Secondary = "Secondary",
  Tertiary = "Tertiary",
  Service = "Service",
  Pedestrian = "Pedestrian",
  Sidewalk = "Sidewalk",
  FootPath = "FootPath",
  Platform = "Platform",
  Unclassified = "Unclassified",
}

export interface UpdateHlaWay {
  type: HlaWayType;
  averageDailyTraffic: number | null;
  averageRoadSpeed: number | null;
  heavyVehicleFlow: HeavyVehicleFlowType | null;
  lit: boolean;
  verified: boolean;
  frontFacingLane: CreateOrUpdateHlaLane | null;
  backFacingLane: CreateOrUpdateHlaLane | null;
}

export const buildEmptyUpdateHlaWay = (): UpdateHlaWay => ({
  type: HlaWayType.Unclassified,
  averageDailyTraffic: null,
  averageRoadSpeed: null,
  heavyVehicleFlow: null,
  lit: true,
  verified: false,
  frontFacingLane: null,
  backFacingLane: null,
});

export const buildEmptyCreateHlaWay = (): CreateHlaWay => ({
  campaignId: "",
  type: HlaWayType.Unclassified,
  name: "Unknown street",
  coordinates: [],
  lengthM: 0,
  averageDailyTraffic: null,
  averageRoadSpeed: null,
  heavyVehicleFlow: null,
  lit: true,
  verified: false,
  frontFacingLane: null,
  backFacingLane: null,
});

export const hlaWayToUpdate = (way: HlaWay): UpdateHlaWay => ({
  type: way.type,
  averageDailyTraffic: way.averageDailyTraffic,
  averageRoadSpeed: way.averageRoadSpeed,
  heavyVehicleFlow: way.heavyVehicleFlow,
  lit: way.lit,
  verified: way.verified,
  frontFacingLane: hlaLaneToCreateOrUpdate(way.frontFacingLane),
  backFacingLane: hlaLaneToCreateOrUpdate(way.backFacingLane),
});

export const hlaLaneToCreateOrUpdate = (
  lane: HlaLane | null,
): CreateOrUpdateHlaLane | null =>
  lane
    ? {
        frontFacing: lane.frontFacing,
        adjacentFeaturesLeft: lane.adjacentFeaturesLeft,
        adjacentFeaturesRight: lane.adjacentFeaturesRight,
        flowDirection: lane.flowDirection,
        facilityType: lane.facilityType,
        facilityWidthPerDirection: lane.facilityWidthPerDirection,
        lightSegregation: lane.lightSegregation,
        tramOrTrainRails: lane.tramOrTrainRails,
      }
    : null;

export interface CreateHlaWay {
  campaignId: string;
  coordinates: number[][];
  lengthM: number;
  name: string;
  type: HlaWayType;
  averageDailyTraffic: number | null;
  averageRoadSpeed: number | null;
  heavyVehicleFlow: HeavyVehicleFlowType | null;
  lit: boolean;
  verified: boolean;
  frontFacingLane: CreateOrUpdateHlaLane | null;
  backFacingLane: CreateOrUpdateHlaLane | null;
}

export const getDefaultAverageRoadSpeed = (
  wayType: HlaWayType,
  distanceUnit: DistanceUnit,
): number => {
  switch (wayType) {
    case "Service":
    case "Unclassified":
      return distanceUnit === "Metric" ? 30 : 20;

    case "Secondary":
    case "Tertiary":
      return distanceUnit === "Metric" ? 50 : 30;

    case "Primary":
      return distanceUnit === "Metric" ? 70 : 40;

    case "Motorway":
      return distanceUnit === "Metric" ? 100 : 60;

    default:
      return 0;
  }
};

export const getDefaultAverageDailyTraffic = (wayType: HlaWayType): number => {
  switch (wayType) {
    case "Service":
    case "Unclassified":
      return 500;
    case "Tertiary":
      return 2500;
    case "Secondary":
      return 5000;
    case "Primary":
      return 10000;
    case "Motorway":
      return 40000;
    default:
      return 0;
  }
};
